import { API_CONFIG, buildApiUrl, ApiResponse, LoginResponse, UserCompaniesGamesResponse, API_RETRY_CONFIG } from './api-config';
import { dialogue_line, GameMember } from '@/types-games';
import { GameProject } from './api-auth';
import { TypeElement } from '../stores/typesStore';
import { BlueprintData } from '@/components/wizards/blueprints';
import { sectionDataObject } from "../stores/objectsStore";
import { DocumentData } from '../stores/documentStore';
import { UserContextData, UserContextSummaryData, CompanyContextData, Version } from '../stores/storeTypes';
import { GameFolderSection } from '../stores/folderStore';
import { getSelectedGame } from '../stores/unifiedUserStore';
import { requestTracker } from './request-tracker';
import { showApiErrorNotification, showBlockedRequestNotification } from '../utils/api-error-notifications';
import { tokenRefreshService } from './token-refresh-service';
import { supabase } from '../config/supabase';

class ApiService {

  private baseUrl = API_CONFIG.BASE_URL;

  // Generic API request method with retry logic and failure tracking
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    silent: boolean = false // Add silent parameter for background operations
  ): Promise<ApiResponse<T>> {
    // Check if this endpoint is currently blocked due to recent failures
    if (requestTracker.shouldBlockRequest(endpoint)) {
      const lastError = requestTracker.getLastError(endpoint) || 'Request recently failed';
      // Only show block notification once per block period, and not for silent requests
      if (!silent && requestTracker.shouldShowBlockNotification(endpoint)) {
        showBlockedRequestNotification(endpoint);
      }
      return {
        success: false,
        error: lastError,
      };
    }

    // Check if we're already retrying this endpoint
    if (requestTracker.isRetrying(endpoint)) {
      return {
        success: false,
        error: 'Request is currently being retried',
      };
    }

    const url = buildApiUrl(endpoint);

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if token exists
    const token = this.getStoredToken();
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    } else {
      console.log('apiService: No auth token available');
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    // Helper function to make the actual request
    const makeRequest = async (): Promise<ApiResponse<T>> => {
      try {
        const response = await fetch(url, config);

        const data = await response.json();

        // if the response is not ok, return the error
        if (!response.ok) {
          // Handle 401 Unauthorized silently - clear token and let ProtectedRoute redirect
          if (response.status === 401) {
            console.log('apiService: 401 Unauthorized - clearing token');
            this.clearStoredToken();
            return {
              success: false,
              error: 'Unauthorized',
              statusCode: 401,
            };
          }

          // Handle different error response formats
          let errorMessage = 'Request failed';

          if (data.message) {
            errorMessage = data.message;
          } else if (data.error) {
            errorMessage = data.error;
          } else if (typeof data === 'string') {
            errorMessage = data;
          }

          console.log('apiService: Request failed with error:', errorMessage);
          return {
            success: false,
            error: errorMessage,
            statusCode: response.status,
            data: data.data, // Include any data from the error response (e.g., lock info on 409)
          };
        }
        return {
          success: true,
          data: data.data,
        };
      } catch (error) {
        console.error("API Service: Network error:", error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Network error',
        };
      }
    };

    // Make the first request attempt
    const firstAttempt = await makeRequest();

    // If successful, clear any failure tracking
    if (firstAttempt.success) {
      requestTracker.markSuccess(endpoint);
      return firstAttempt;
    }

    // First attempt failed
    // Don't retry or show notifications for 401 errors - just return silently
    if ((firstAttempt as any).statusCode === 401) {
      return firstAttempt;
    }

    // Don't retry or show notifications for 409 conflicts (lock conflicts are expected)
    if ((firstAttempt as any).statusCode === 409) {
      console.log(`apiService: Request to ${endpoint} returned 409 Conflict (expected for lock conflicts)`);
      return firstAttempt;
    }

    const retryCount = requestTracker.getRetryCount(endpoint);
    
    // If we haven't retried yet, attempt a retry after delay
    if (retryCount < API_RETRY_CONFIG.MAX_RETRY_ATTEMPTS) {
      console.log(`apiService: Request to ${endpoint} failed. Retrying in ${API_RETRY_CONFIG.RETRY_DELAY_MS}ms...`);
      
      // Mark as failed and retrying
      requestTracker.markFailed(endpoint, firstAttempt.error);
      requestTracker.markRetrying(endpoint);

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, API_RETRY_CONFIG.RETRY_DELAY_MS));

      // Make retry attempt
      const retryAttempt = await makeRequest();

      // If retry successful, clear failure tracking
      if (retryAttempt.success) {
        requestTracker.markSuccess(endpoint);
        console.log(`apiService: Retry for ${endpoint} succeeded`);
        return retryAttempt;
      }

      // Don't show notification for 401 errors or 409 conflicts on retry either, or for silent requests
      if ((retryAttempt as any).statusCode !== 401 && (retryAttempt as any).statusCode !== 409 && !silent) {
        // Retry failed - mark as failed again and show notification
        requestTracker.markFailed(endpoint, retryAttempt.error);
        showApiErrorNotification(endpoint, retryAttempt.error || 'Unknown error', true);
      }
      
      return retryAttempt;
    }

    // Already retried before, just mark as failed and show notification (but not for 401/409 or silent requests)
    if ((firstAttempt as any).statusCode !== 409 && !silent) {
      requestTracker.markFailed(endpoint, firstAttempt.error);
      showApiErrorNotification(endpoint, firstAttempt.error || 'Unknown error', false);
    }
    
    return firstAttempt;
  }

  // Authentication methods
  async login(email: string, password: string): Promise<ApiResponse<LoginResponse>> {

    const result = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    return result;
  }

  async signUp(email: string, password: string): Promise<ApiResponse<{ message: string }>> {
    const result = await this.request<{ message: string }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    return result;
  }

  async logout(): Promise<ApiResponse> {
    const result = await this.request('/auth/logout', {
      method: 'POST',
    });

    // Clear stored token regardless of API response
    this.clearStoredToken();

    return result;
  }

  async refreshToken(): Promise<ApiResponse<LoginResponse>> {
    try {
      // Get the current Supabase session to obtain the refresh token
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session?.refresh_token) {
        console.error('TokenRefresh: No Supabase session available for refresh');
        return {
          success: false,
          error: 'No refresh token available',
        };
      }

      // Call the refresh endpoint with the Supabase refresh token
      const response = await this.request<LoginResponse>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({
          refreshToken: sessionData.session.refresh_token,
        }),
      });

      return response;
    } catch (error) {
      console.error('TokenRefresh: Refresh failed with error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Token refresh failed',
      };
    }
  }

  // Token management
  private getStoredToken(): string | null {
    // Check localStorage first (persistent - "keep me logged in")
    const persistentToken = localStorage.getItem('auth_token');
    if (persistentToken) {
      return persistentToken;
    }
    // Fall back to sessionStorage (session only - cleared when browser closes)
    return sessionStorage.getItem('auth_token');
  }

  private setStoredToken(token: string, persistent: boolean = false): void {
    if (persistent) {
      // Store in localStorage for persistent login
      localStorage.setItem('auth_token', token);
      // Remove from sessionStorage if it exists
      sessionStorage.removeItem('auth_token');
    } else {
      // Store in sessionStorage for session-only login
      sessionStorage.setItem('auth_token', token);
      // Remove from localStorage if it exists
      localStorage.removeItem('auth_token');
    }

    // Schedule automatic token refresh
    this.scheduleTokenRefresh(token);
  }

  private clearStoredToken(): void {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
    
    // Stop automatic token refresh
    tokenRefreshService.stopRefresh();
  }

  // Public method to store token after successful login
  storeToken(token: string, rememberMe: boolean = false): void {
    this.setStoredToken(token, rememberMe);
  }

  // Public method to get token for other parts of the app
  getToken(): string | null {
    return this.getStoredToken();
  }

  // Public method to clear token
  clearToken(): void {
    this.clearStoredToken();
  }

  // Schedule automatic token refresh
  private scheduleTokenRefresh(token: string): void {
    // Get token info for logging
    const tokenInfo = tokenRefreshService.getTokenInfo(token);
    if (tokenInfo) {
      console.log('TokenRefresh: Token will refresh in', tokenInfo.timeUntilRefresh, '(expires in', tokenInfo.timeUntilExpiration + ')');
    }

    // Schedule the refresh with callbacks
    tokenRefreshService.scheduleRefresh(
      token,
      // Refresh callback - performs the actual token refresh
      async () => {
        console.log('TokenRefresh: Executing scheduled refresh...');
        const response = await this.refreshToken();
        
        if (!response.success || !response.data?.token) {
          throw new Error('Token refresh failed: ' + (response.error || 'Unknown error'));
        }

        // Store the new token (this will also schedule the next refresh)
        const currentToken = this.getStoredToken();
        const isPersistent = localStorage.getItem('auth_token') !== null;
        this.storeToken(response.data.token, isPersistent);
        
        console.log('TokenRefresh: Token refreshed and next refresh scheduled');
      },
      // Logout callback - called if refresh fails
      () => {
        console.log('TokenRefresh: Refresh failed, clearing session');
        this.clearStoredToken();
        
        // Trigger a page reload to redirect to login
        // This is safe because the token is already cleared
        window.location.href = '/login';
      }
    );
  }

  // Public method to get token info (for debugging)
  getTokenInfo(): ReturnType<typeof tokenRefreshService.getTokenInfo> | null {
    const token = this.getStoredToken();
    if (!token) return null;
    return tokenRefreshService.getTokenInfo(token);
  }

  // Public method to manually trigger token refresh (for testing)
  async manualRefresh(): Promise<void> {
    console.log('TokenRefresh: Manual refresh requested');
    
    // Call refresh directly instead of going through tokenRefreshService
    // This avoids the logout callback if refresh fails
    const response = await this.refreshToken();
    
    if (!response.success || !response.data?.token) {
      console.error('TokenRefresh: Manual refresh failed:', response.error);
      throw new Error('Token refresh failed: ' + (response.error || 'Unknown error'));
    }

    // Store the new token, maintaining persistence preference
    const isPersistent = localStorage.getItem('auth_token') !== null;
    this.storeToken(response.data.token, isPersistent);
    
    console.log('TokenRefresh: Manual refresh completed successfully');
  }

  async fetchDialogueLines(gameId: string): Promise<ApiResponse<dialogue_line[]>> {
    return this.request<dialogue_line[]>(`${API_CONFIG.ENDPOINTS.DATA.DIALOGUE_LINES}?game_id=${gameId}`, {
      method: 'GET',
    });
  }

  async fetchStringsItems(gameId: string): Promise<ApiResponse<Array<{
    id: string;
    version_id: string;
    game_id: string;
    source_text: string;
    section: string;
    context: string;
    metadata: Record<string, unknown>[];
    abbrev: string;
    status: string;
    created_at: string;
    updated_at: string;
    updated_by: string;
    created_by: string;
    translations: Array<{
      id: string;
      dialogue_line_id: string;
      language_code: string;
      translated_text: string;
      approved: boolean;
      created_at: string;
      updated_at: string;
    }>;
  }>>> {
    return this.request<Array<{
      id: string;
      version_id: string;
      game_id: string;
      source_text: string;
      section: string;
      context: string;
      metadata: Record<string, unknown>[];
      abbrev: string;
      status: string;
      created_at: string;
      updated_at: string;
      updated_by: string;
      created_by: string;
      translations: Array<{
        id: string;
        dialogue_line_id: string;
        language_code: string;
        translated_text: string;
        approved: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>>(`/strings-original?game_id=${gameId}`, {
      method: 'GET',
    });
  }

  async createString(stringData: {
    game_id: string;
    source_text: string;
    section?: string;
    context?: string;
    metadata?: Record<string, unknown>[];
    abbrev?: string;
    status?: string;
  }): Promise<ApiResponse<{
    id: string;
    version_id: string;
    game_id: string;
    source_text: string;
    section: string;
    context: string;
    metadata: Record<string, unknown>[];
    abbrev: string;
    status: string;
    created_at: string;
    updated_at: string;
    updated_by: string;
    created_by: string;
    translations: Array<{
      id: string;
      dialogue_line_id: string;
      language_code: string;
      translated_text: string;
      approved: boolean;
      created_at: string;
      updated_at: string;
    }>;
  }>> {
    return this.request<{
      id: string;
      version_id: string;
      game_id: string;
      source_text: string;
      section: string;
      context: string;
      metadata: Record<string, unknown>[];
      abbrev: string;
      status: string;
      created_at: string;
      updated_at: string;
      updated_by: string;
      created_by: string;
      translations: Array<{
        id: string;
        dialogue_line_id: string;
        language_code: string;
        translated_text: string;
        approved: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>('/strings-original', {
      method: 'POST',
      body: JSON.stringify(stringData),
    });
  }

  async updateStringItem(item: {
    id: string;
    game_id: string;
    source_text?: string;
    section?: string;
    context?: string;
    abbrev?: string;
    status?: string;
  }): Promise<ApiResponse<{
    id: string;
    version_id: string;
    game_id: string;
    source_text: string;
    section: string;
    context: string;
    metadata: Record<string, unknown>[];
    abbrev: string;
    status: string;
    created_at: string;
    updated_at: string;
    updated_by: string;
    created_by: string;
    translations: Array<{
      id: string;
      dialogue_line_id: string;
      language_code: string;
      translated_text: string;
      approved: boolean;
      created_at: string;
      updated_at: string;
    }>;
  }>> {
    return this.request<{
      id: string;
      version_id: string;
      game_id: string;
      source_text: string;
      section: string;
      context: string;
      metadata: Record<string, unknown>[];
      abbrev: string;
      status: string;
      created_at: string;
      updated_at: string;
      updated_by: string;
      created_by: string;
      translations: Array<{
        id: string;
        dialogue_line_id: string;
        language_code: string;
        translated_text: string;
        approved: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>(`${API_CONFIG.ENDPOINTS.DATA.STRINGS_ORIGINAL}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async updateTranslationLine(
    translationId: string,
    translationData: {
      language_code: string;
      translated_text: string;
      approved: boolean;
    }
  ): Promise<ApiResponse<{
    id: string;
    dialogue_line_id: string;
    language_code: string;
    translated_text: string;
    approved: boolean;
    created_at: string;
    updated_at: string;
  }>> {
    console.log('🌐 apiService: updateTranslationLine called', {
      translationId,
      translationData,
      endpoint: `${API_CONFIG.ENDPOINTS.DATA.TRANSLATION_LINES}/${translationId}`,
      timestamp: new Date().toISOString()
    });

    const result = await this.request<{
      id: string;
      dialogue_line_id: string;
      language_code: string;
      translated_text: string;
      approved: boolean;
      created_at: string;
      updated_at: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.TRANSLATION_LINES}/${translationId}`, {
      method: 'PUT',
      body: JSON.stringify(translationData),
    });

    console.log('🌐 apiService: updateTranslationLine response', {
      success: result.success,
      data: result.data,
      error: result.error,
      originalData: translationData
    });

    return result;
  }

  async createTranslationLine(
    translationData: {
      dialogue_line_id: string;
      language_code: string;
      translated_text?: string;
      approved?: boolean;
    }
  ): Promise<ApiResponse<{
    id: string;
    dialogue_line_id: string;
    language_code: string;
    translated_text: string;
    approved: boolean;
    created_at: string;
    updated_at: string;
  }>> {
    console.log('🌐 apiService: createTranslationLine called', {
      translationData,
      isEmptyText: !translationData.translated_text || translationData.translated_text.trim() === '',
      endpoint: `${API_CONFIG.ENDPOINTS.DATA.TRANSLATION_LINES}`,
      timestamp: new Date().toISOString()
    });

    const result = await this.request<{
      id: string;
      dialogue_line_id: string;
      language_code: string;
      translated_text: string;
      approved: boolean;
      created_at: string;
      updated_at: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.TRANSLATION_LINES}`, {
      method: 'POST',
      body: JSON.stringify(translationData),
    });

    console.log('🌐 apiService: createTranslationLine response', {
      success: result.success,
      data: result.data,
      error: result.error,
      originalData: translationData
    });

    return result;
  }

  async fetchObjects(gameId: string, folder?: string | null): Promise<ApiResponse<Array<{
    id: string;
    game_id: string;
    name: string;
    type: string; // Add type field
    tags: string[];
    data: sectionDataObject[];
    blueprint_id?: string | null;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    blueprint_version?: number;
    identifiers?: string[];
    folder?: string | null;
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
    description?: string;
    created_at: string;
    updated_at: string;
  }>>> {
    let url = `${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_ITEMS}?game_id=${gameId}`;
    if (folder !== undefined) {
      url += `&folder=${encodeURIComponent(folder || 'null')}`;
    }
    
    return this.request<Array<{
      id: string;
      game_id: string;
      name: string;
      type: string; // Add type field
      tags: string[];
      data: sectionDataObject[];
      blueprint_id?: string | null;
      version?: {
        changes: Record<string, unknown>;
        version: number;
      };
      blueprint_version?: number;
      identifiers?: string[];
      folder?: string | null;
      owner?: string | null;
      edit_permissions?: 'Open' | 'Locked';
      description?: string;
      created_at: string;
      updated_at: string;
    }>>(url, {
      method: 'GET',
    });
  }

  async createObject(item: {
    game_id: string;
    name: string;
    type: string;
    data: sectionDataObject[];
    tags?: string[];
    blueprint_id?: string;
    version?: Version;
    blueprint_version?: number | Version;
    identifiers?: string[];
    folder?: string | null;
    status?: string;
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
    description?: string;
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    type: string;
    tags: string[];
    data: sectionDataObject[];
    blueprint_id?: string | null;
    version?: Version;
    blueprint_version?: number | Version;
    identifiers?: string[];
    folder?: string | null;
    status?: string;
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
    description?: string;
    created_at: string;
    updated_at: string;
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      type: string;
      tags: string[];
      data: sectionDataObject[];
      blueprint_id?: string | null;
      version?: Version;
      blueprint_version?: number | Version;
      folder?: string | null;
      status?: string;
      owner?: string | null;
      edit_permissions?: 'Open' | 'Locked';
      created_at: string;
      updated_at: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_ITEMS}`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateObject(item: {
    id: string;
    game_id: string;
    name: string;
    type: string;
    data: sectionDataObject[];
    tags?: string[];
    blueprint_id?: string;
    version?: Version;
    blueprint_version?: number | Version;
    identifiers?: string[];
    folder?: string | null;
    status?: string;
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
    description?: string;
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    type: string;
    tags: string[];
    data: sectionDataObject[];
    blueprint_id?: string | null;
    version?: Version;
    blueprint_version?: number | Version;
    identifiers?: string[];
    folder?: string | null;
    status?: string;
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
    description?: string;
    created_at: string;
    updated_at: string;
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      type: string;
      tags: string[];
      data: sectionDataObject[];
      blueprint_id?: string | null;
      version?: Version;
      blueprint_version?: number | Version;
      identifiers?: string[];
      folder?: string | null;
      status?: string;
      owner?: string | null;
      edit_permissions?: 'Open' | 'Locked';
      created_at: string;
      updated_at: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_ITEMS}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async fetchMigrationPreview(objectId: string, targetVersion?: Version): Promise<ApiResponse<any>> {
    let url = `/blueprint-migration-preview?object_id=${objectId}`;
    if (targetVersion) {
      url += `&target_major=${targetVersion.major}&target_minor=${targetVersion.minor}&target_patch=${targetVersion.patch}`;
    }
    
    return this.request<any>(url, {
      method: 'GET',
    });
  }

  async migrateObject(
    objectId: string,
    targetVersion: Version,
    strategy: 'merge' | 'overwrite'
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/blueprint-migrate', {
      method: 'POST',
      body: JSON.stringify({
        object_id: objectId,
        target_version: targetVersion,
        strategy: strategy,
      }),
    });
  }

  async fetchDocumentItems(gameId: string): Promise<ApiResponse<Array<{
    id: string;
    game_id: string;
    name: string;
    tags: string[];
    data: DocumentData[];
    settings?: Record<string, unknown>;
    created_at: string;
    updated_at: string;
    created_by?: string;
  }>>> {

    
    const result = await this.request<Array<{
      id: string;
      game_id: string;
      name: string;
      tags: string[];
      data: DocumentData[];
      settings?: Record<string, unknown>;
      created_at: string;
      updated_at: string;
      created_by?: string;
    }>>(`${API_CONFIG.ENDPOINTS.DATA.DOCUMENT_ITEMS}?game_id=${gameId}`, {
      method: 'GET',
    });
    
    return result;
  }

  async createDocumentItem(item: {
    game_id: string;
    name: string;
    data: DocumentData[];
    settings?: Record<string, unknown>;
    tags?: string[];
    folder?: string | null;
    status?: 'draft' | 'published';
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    tags: string[];
    data: DocumentData[];
    settings?: Record<string, unknown>;
    folder?: string | null;
    status?: 'draft' | 'published';
    created_at: string;
    updated_at: string;
    created_by?: string;
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      tags: string[];
      data: DocumentData[];
      settings?: Record<string, unknown>;
      folder?: string | null;
      status?: 'draft' | 'published';
      created_at: string;
      updated_at: string;
      created_by?: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.DOCUMENT_ITEMS}`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateDocumentItem(item: {
    id: string;
    game_id: string;
    name: string;
    data: DocumentData[];
    settings?: Record<string, unknown>;
    tags?: string[];
    folder?: string | null;
    status?: 'draft' | 'published';
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    tags: string[];
    data: DocumentData[];
    settings?: Record<string, unknown>;
    folder?: string | null;
    status?: 'draft' | 'published';
    created_at: string;
    updated_at: string;
    created_by?: string;
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      tags: string[];
      data: DocumentData[];
      settings?: Record<string, unknown>;
      folder?: string | null;
      status?: 'draft' | 'published';
      created_at: string;
      updated_at: string;
      created_by?: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.DOCUMENT_ITEMS}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async deleteDocumentItem(id: string, gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`${API_CONFIG.ENDPOINTS.DATA.DOCUMENT_ITEMS}?id=${id}&game_id=${gameId}`, {
      method: 'DELETE',
    });
  }

  // UI Elements methods
  async fetchUIElements(gameId: string): Promise<ApiResponse<Array<{
    id: string;
    game_id: string;
    name: string;
    html: string;
    css: string;
    tags: string[];
    status?: 'draft' | 'published';
    created_at: string;
    updated_at: string;
    created_by?: string;
  }>>> {
    const result = await this.request<Array<{
      id: string;
      game_id: string;
      name: string;
      html: string;
      css: string;
      tags: string[];
      status?: 'draft' | 'published';
      created_at: string;
      updated_at: string;
      created_by?: string;
    }>>(`${API_CONFIG.ENDPOINTS.DATA.UI_ELEMENTS}?game_id=${gameId}`, {
      method: 'GET',
    });
    
    return result;
  }

  async createUIElement(item: {
    game_id: string;
    name: string;
    html: string;
    css?: string;
    tags?: string[];
    status?: 'draft' | 'published';
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    html: string;
    css: string;
    tags: string[];
    status?: 'draft' | 'published';
    created_at: string;
    updated_at: string;
    created_by?: string;
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      html: string;
      css: string;
      tags: string[];
      status?: 'draft' | 'published';
      created_at: string;
      updated_at: string;
      created_by?: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.UI_ELEMENTS}`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateUIElement(item: {
    id: string;
    game_id: string;
    name: string;
    html: string;
    css?: string;
    tags?: string[];
    status?: 'draft' | 'published';
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    html: string;
    css: string;
    tags: string[];
    status?: 'draft' | 'published';
    created_at: string;
    updated_at: string;
    created_by?: string;
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      html: string;
      css: string;
      tags: string[];
      status?: 'draft' | 'published';
      created_at: string;
      updated_at: string;
      created_by?: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.UI_ELEMENTS}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    });
  }

  async deleteUIElement(id: string, gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`${API_CONFIG.ENDPOINTS.DATA.UI_ELEMENTS}?id=${id}&game_id=${gameId}`, {
      method: 'DELETE',
    });
  }

  async fetchBlueprints(gameId: string, folder?: string | null): Promise<ApiResponse<Array<{
    id: string;
    game_id: string;
    name: string;
    tags: string[];
    type: string;
    description: string;
    data: Record<string, unknown>;
    settings: Record<string, unknown>;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    folder?: string | null;
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
    created_at: string;
    updated_at: string;
    created_by: string;
    updated_by: string;
  }>>> {
    let url = `${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_BLUEPRINTS}?game_id=${gameId}`;
    if (folder !== undefined) {
      url += `&folder=${encodeURIComponent(folder || 'null')}`;
    }
    return this.request<Array<{
      id: string;
      game_id: string;
      name: string;
      tags: string[];
      type: string;
      description: string;
      data: Record<string, unknown>;
      settings: Record<string, unknown>;
      folder?: string | null;
      owner?: string | null;
      edit_permissions?: 'Open' | 'Locked';
      created_at: string;
      updated_at: string;
      created_by: string;
      updated_by: string;
    }>>(url, {
      method: 'GET',
    });
  }

  async updateBlueprint(blueprint: {
    id: string;
    game_id: string;
    name: string;
    tags: string[];
    type: string;
    description: string;
    data: BlueprintData;
    settings?: Record<string, unknown>;
    version?: 
      | { changes: Record<string, unknown>; version: number }
      | { major: number; minor: number; patch: number };
    folder?: string | null; // Add folder field
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    tags: string[];
    type: string;
    description: string;
    data: Record<string, unknown>;
    settings: Record<string, unknown>;
    version?: 
      | { changes: Record<string, unknown>; version: number }
      | { major: number; minor: number; patch: number };
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
    created_at: string;
    updated_at: string;
    created_by: string;
    updated_by: string;
  }>> {
    const result = await this.request<{
      id: string;
      game_id: string;
      name: string;
      tags: string[];
      type: string;
      description: string;
      data: Record<string, unknown>;
      settings: Record<string, unknown>;
      owner?: string | null;
      edit_permissions?: 'Open' | 'Locked';
      created_at: string;
      updated_at: string;
      created_by: string;
      updated_by: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_BLUEPRINTS}`, {
      method: 'PUT',
      body: JSON.stringify(blueprint),
    });

    return result;
  }

  async createBlueprint(blueprint: {
    game_id: string;
    name: string;
    type: string;
    tags: string[];
    description: string;
    data: BlueprintData;
    settings?: Record<string, unknown>;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    folder?: string | null; // Add folder field
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    type: string;
    tags: string[];
    description: string;
    data: Record<string, unknown>;
    settings: Record<string, unknown>;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
    created_at: string;
    updated_at: string;
    created_by: string;
    updated_by: string;
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      tags: string[];
      type: string;
      description: string;
      data: Record<string, unknown>;
      settings: Record<string, unknown>;
      owner?: string | null;
      edit_permissions?: 'Open' | 'Locked';
      created_at: string;
      updated_at: string;
      created_by: string;
      updated_by: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_BLUEPRINTS}`, {
      method: 'POST',
      body: JSON.stringify(blueprint),
    });
  }

  async deleteTemplate(id: string, gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_BLUEPRINTS}?id=${encodeURIComponent(id)}&game_id=${encodeURIComponent(gameId)}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Blueprint Version Control API Methods
  // ============================================================================

  /**
   * Fetch version history for a blueprint
   */
  async fetchBlueprintVersions(
    blueprintId: string,
    majorVersion?: number,
    limit: number = 20,
    offset: number = 0
  ): Promise<ApiResponse<any>> {
    let url = `${API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_VERSIONS}?blueprint_id=${blueprintId}&limit=${limit}&offset=${offset}`;
    if (majorVersion !== undefined) {
      url += `&major_version=${majorVersion}`;
    }
    return this.request<any>(url, { method: 'GET' });
  }

  /**
   * Fetch specific version data (reconstructed)
   */
  async fetchVersionData(versionId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`${API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_VERSIONS}?version_id=${versionId}`, {
      method: 'GET',
    });
  }

  /**
   * Fetch version by version numbers
   */
  async fetchVersionByNumbers(
    blueprintId: string,
    major: number,
    minor: number,
    patch: number
  ): Promise<ApiResponse<any>> {
    return this.request<any>(
      `${API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_VERSIONS}?blueprint_id=${blueprintId}&major=${major}&minor=${minor}&patch=${patch}`,
      { method: 'GET' }
    );
  }

  /**
   * Create a new version (typically called automatically on save)
   */
  async createBlueprintVersion(data: {
    blueprint_id: string;
    blueprint_data: any;
    old_data?: any;
    version: { major: number; minor: number; patch: number };
    change_summary?: string;
  }): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_VERSIONS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a specific version
   */
  async deleteBlueprintVersion(versionId: string, blueprintId: string): Promise<ApiResponse<any>> {
    return this.request<any>(
      `${API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_VERSIONS}?version_id=${versionId}&blueprint_id=${blueprintId}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Restore blueprint to a previous version
   */
  async restoreBlueprintVersion(
    blueprintId: string,
    versionId: string
  ): Promise<ApiResponse<{
    restored_from: string;
    new_version: string;
  }>> {
    return this.request<{
      restored_from: string;
      new_version: string;
    }>(API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_VERSIONS, {
      method: 'POST',
      body: JSON.stringify({
        action: 'restore',
        blueprint_id: blueprintId,
        version_id: versionId,
      }),
    });
  }

  // ============================================================================
  // Blueprint Migration API Methods
  // ============================================================================

  /**
   * Check if upgrade is available for a glossary item
   */
  async checkBlueprintUpgrade(glossaryItemId: string): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_MIGRATIONS, {
      method: 'POST',
      body: JSON.stringify({
        action: 'check',
        glossary_item_id: glossaryItemId,
      }),
    });
  }

  /**
   * Preview upgrade changes for a glossary item
   */
  async previewBlueprintUpgrade(
    glossaryItemId: string,
    targetVersion?: { major: number; minor: number; patch: number }
  ): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_MIGRATIONS, {
      method: 'POST',
      body: JSON.stringify({
        action: 'preview',
        glossary_item_id: glossaryItemId,
        target_version: targetVersion,
      }),
    });
  }

  /**
   * Perform upgrade on a glossary item
   */
  async upgradeBlueprintItem(
    glossaryItemId: string,
    targetVersion?: { major: number; minor: number; patch: number },
    strategy: 'merge' | 'overwrite' = 'merge'
  ): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_MIGRATIONS, {
      method: 'POST',
      body: JSON.stringify({
        action: 'upgrade',
        glossary_item_id: glossaryItemId,
        target_version: targetVersion,
        strategy,
      }),
    });
  }

  /**
   * Batch upgrade multiple glossary items
   */
  async batchUpgradeBlueprintItems(
    blueprintId: string,
    fromVersion?: { major: number; minor: number; patch: number },
    toVersion?: { major: number; minor: number; patch: number },
    strategy: 'merge' | 'overwrite' = 'merge'
  ): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.BLUEPRINT_MIGRATIONS, {
      method: 'POST',
      body: JSON.stringify({
        action: 'batch-upgrade',
        blueprint_id: blueprintId,
        from_version: fromVersion,
        to_version: toVersion,
        strategy,
      }),
    });
  }

  // ============================================================================
  // Glossary Version Control API Methods
  // ============================================================================

  /**
   * Fetch version history for a glossary item
   */
  async fetchGlossaryVersions(
    glossaryId: string,
    majorVersion?: number,
    limit: number = 20,
    offset: number = 0
  ): Promise<ApiResponse<any>> {
    let url = `${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_VERSIONS}?glossary_id=${glossaryId}&limit=${limit}&offset=${offset}`;
    if (majorVersion !== undefined) {
      url += `&major_version=${majorVersion}`;
    }
    return this.request<any>(url, { method: 'GET' });
  }

  /**
   * Fetch specific glossary version data (reconstructed)
   */
  async fetchGlossaryVersionData(versionId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_VERSIONS}?version_id=${versionId}`, {
      method: 'GET',
    });
  }

  /**
   * Fetch glossary version by version numbers
   */
  async fetchGlossaryVersionByNumbers(
    glossaryId: string,
    major: number,
    minor: number,
    patch: number
  ): Promise<ApiResponse<any>> {
    return this.request<any>(
      `${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_VERSIONS}?glossary_id=${glossaryId}&major=${major}&minor=${minor}&patch=${patch}`,
      { method: 'GET' }
    );
  }

  /**
   * Create a new glossary version (typically called automatically on save)
   */
  async createGlossaryVersion(data: {
    glossary_id: string;
    glossary_data: any;
    old_data?: any;
    version: { major: number; minor: number; patch: number };
    change_summary?: string;
  }): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.GLOSSARY_VERSIONS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a specific glossary version
   */
  async deleteGlossaryVersion(versionId: string, glossaryId: string): Promise<ApiResponse<any>> {
    return this.request<any>(
      `${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_VERSIONS}?version_id=${versionId}&glossary_id=${glossaryId}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Restore glossary item to a previous version
   */
  async restoreGlossaryVersion(
    glossaryId: string,
    versionId: string,
    gameId?: string
  ): Promise<ApiResponse<{
    restored_from: string;
    new_version: string;
    item: any;
  }>> {
    return this.request<{
      restored_from: string;
      new_version: string;
      item: any;
    }>(API_CONFIG.ENDPOINTS.DATA.GLOSSARY_VERSIONS, {
      method: 'POST',
      body: JSON.stringify({
        action: 'restore',
        glossary_id: glossaryId,
        version_id: versionId,
        game_id: gameId,
      }),
    });
  }

  async deleteObject(id: string, gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`${API_CONFIG.ENDPOINTS.DATA.GLOSSARY_ITEMS}?id=${id}&game_id=${gameId}`, {
      method: 'DELETE',
    });
  }

  async deleteStringItem(id: string, gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`${API_CONFIG.ENDPOINTS.DATA.STRINGS_ORIGINAL}?id=${id}&game_id=${gameId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Dialog API Methods
  // ============================================================================

  /**
   * Fetch dialogs for a specific game
   */
  async fetchDialogs(gameId: string, folder?: string | null): Promise<ApiResponse<Array<any>>> {
    let url = `${API_CONFIG.ENDPOINTS.DATA.DIALOGS}?game_id=${gameId}`;
    
    if (folder !== undefined && folder !== null) {
      url += `&folder=${folder}`;
    }
    
    return this.request<Array<any>>(url, {
      method: 'GET',
    });
  }

  /**
   * Fetch a single dialog with full details (sections and options)
   */
  async fetchDialog(dialogId: string, gameId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`${API_CONFIG.ENDPOINTS.DATA.DIALOGS}?game_id=${gameId}&dialog_id=${dialogId}`, {
      method: 'GET',
    });
  }

  /**
   * Create a new dialog
   */
  async createDialog(dialog: any): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.DIALOGS, {
      method: 'POST',
      body: JSON.stringify(dialog),
    });
  }

  /**
   * Update an existing dialog
   */
  async updateDialog(dialog: any): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.DIALOGS, {
      method: 'PUT',
      body: JSON.stringify(dialog),
    });
  }

  /**
   * Delete a dialog
   */
  async deleteDialog(id: string, gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`${API_CONFIG.ENDPOINTS.DATA.DIALOGS}?id=${id}&game_id=${gameId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Logic Flow API Methods
  // ============================================================================

  /**
   * Fetch logic flows for a specific game
   */
  async fetchLogicFlows(gameId: string): Promise<ApiResponse<Array<any>>> {
    return this.request<Array<any>>(`${API_CONFIG.ENDPOINTS.DATA.LOGIC_FLOWS}?game_id=${gameId}`, {
      method: 'GET',
    });
  }

  /**
   * Fetch a single logic flow with full details
   */
  async fetchLogicFlow(flowId: string, gameId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`${API_CONFIG.ENDPOINTS.DATA.LOGIC_FLOWS}?id=${flowId}`, {
      method: 'GET',
    });
  }

  /**
   * Create a new logic flow
   */
  async createLogicFlow(flow: any): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.LOGIC_FLOWS, {
      method: 'POST',
      body: JSON.stringify(flow),
    });
  }

  /**
   * Update an existing logic flow
   */
  async updateLogicFlow(flow: any): Promise<ApiResponse<any>> {
    return this.request<any>(API_CONFIG.ENDPOINTS.DATA.LOGIC_FLOWS, {
      method: 'PUT',
      body: JSON.stringify(flow),
    });
  }

  /**
   * Delete a logic flow
   */
  async deleteLogicFlow(id: string, gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`${API_CONFIG.ENDPOINTS.DATA.LOGIC_FLOWS}?id=${id}&game_id=${gameId}`, {
      method: 'DELETE',
    });
  }

  async fetchUserCompaniesGames(): Promise<ApiResponse<UserCompaniesGamesResponse[]>> {
    return this.request<UserCompaniesGamesResponse[]>(API_CONFIG.ENDPOINTS.USER.COMPANIES_GAMES, {
      method: 'GET',
    });
  }

  async fetchUsers(companyId: string): Promise<ApiResponse<Array<{
    id: string;
    email: string;
    username: string | null;
    avatar_url: string | null;
    role: string;
    created_at: string;
    updated_at: string;
    company_id: string;
    game_id?: string | null;
  }>>> {
    return this.request<Array<{
      id: string;
      email: string;
      username: string | null;
      avatar_url: string | null;
      role: string;
      created_at: string;
      updated_at: string;
      company_id: string;
      game_id?: string | null;
    }>>(`${API_CONFIG.ENDPOINTS.USER.COMPANY_USERS}?company_id=${companyId}`, {
      method: 'GET',
    });
  }

  async fetchUsersByGame(gameId: string): Promise<ApiResponse<Array<{
    id: string;
    email: string;
    username: string | null;
    avatar_url: string | null;
    role: string;
    created_at: string;
    updated_at: string;
    company_id: string;
    game_id?: string | null;
  }>>> {
    console.log("fetchUsersByGame", gameId)
    return this.request<Array<{
      id: string;
      email: string;
      username: string | null;
      avatar_url: string | null;
      role: string;
      created_at: string;
      updated_at: string;
      company_id: string;
      game_id?: string | null;
    }>>(`${API_CONFIG.ENDPOINTS.USER.COMPANY_USERS}?game_id=${gameId}`, {
      method: 'GET',
    });
  }

  async fetchAuthUsers(userIds: string[]): Promise<ApiResponse<Array<{
    id: string;
    email: string;
    username: string;
    avatar_url: string | null;
    created_at: string;
    updated_at: string;
  }>>> {
    const userIdsParam = userIds.join(',');
    return this.request<Array<{
      id: string;
      email: string;
      username: string;
      avatar_url: string | null;
      created_at: string;
      updated_at: string;
    }>>(`auth-users?user_ids=${encodeURIComponent(userIdsParam)}`, {
      method: 'GET',
    });
  }

  async fetchProfile(): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request<Record<string, unknown>>('/user', {
      method: 'GET',
    });
  }

  async fetchGames(): Promise<ApiResponse<GameProject[]>> {
    return this.request<GameProject[]>('/user/games', {
      method: 'GET',
    });
  }

  async fetchUserContext(): Promise<ApiResponse<UserContextData>> {
    return this.request<UserContextData>('/user-context', {
      method: 'GET',
    });
  }

  /**
   * Fetch lightweight user context summary (user profile + company list only).
   * Used at login to quickly populate the app shell before loading full company data.
   */
  async fetchUserContextSummary(): Promise<ApiResponse<UserContextSummaryData>> {
    return this.request<UserContextSummaryData>('/user-context-summary', {
      method: 'GET',
    });
  }

  /**
   * Fetch full data for a single company (games, members, roles, permissions, profiles).
   * Called after company selection and when switching companies.
   */
  async fetchCompanyContext(companyId: string): Promise<ApiResponse<CompanyContextData>> {
    return this.request<CompanyContextData>(`/user-context-company?companyId=${companyId}`, {
      method: 'GET',
    });
  }

  /**
   * Fetch actual game memberships for a company
   * Returns a map of gameId -> [userIds] based on company_members_games table
   */
  async fetchGameMemberships(companyId: string): Promise<ApiResponse<Record<string, string[]>>> {
    return this.request<Record<string, string[]>>(`/game-memberships?company_id=${companyId}`, {
      method: 'GET',
    });
  }

  // Page Structure methods
  async fetchPageStructures(): Promise<ApiResponse<Array<{
    id: string;
    user_id: string;
    home: Record<string, unknown> | null;
    text_management: Record<string, unknown> | null;
    documents: Record<string, unknown> | null;
    glossary: Record<string, unknown> | null;
    blueprints: Record<string, unknown> | null;
    typesandtags?: Record<string, unknown> | null;
    settings?: Record<string, unknown> | null;
    updated_at: string;
  }>>> {
    return this.request<Array<{
      id: string;
      user_id: string;
      home: Record<string, unknown> | null;
      text_management: Record<string, unknown> | null;
      documents: Record<string, unknown> | null;
      glossary: Record<string, unknown> | null;
      blueprints: Record<string, unknown> | null;
      typesandtags?: Record<string, unknown> | null;
      settings?: Record<string, unknown> | null;
      updated_at: string;
    }>>('/page-structure', {
      method: 'GET',
    });
  }

  async fetchPageStructureById(id: string): Promise<ApiResponse<{
    id: string;
    user_id: string;
    home: Record<string, unknown> | null;
    text_management: Record<string, unknown> | null;
    documents: Record<string, unknown> | null;
    glossary: Record<string, unknown> | null;
    blueprints: Record<string, unknown> | null;
    typesandtags?: Record<string, unknown> | null;
    settings?: Record<string, unknown> | null;
    tms_page?: Record<string, unknown> | null;
    updated_at: string;
  }>> {
    return this.request<{
      id: string;
      user_id: string;
      home: Record<string, unknown> | null;
      text_management: Record<string, unknown> | null;
      documents: Record<string, unknown> | null;
      glossary: Record<string, unknown> | null;
      blueprints: Record<string, unknown> | null;
      typesandtags?: Record<string, unknown> | null;
      settings?: Record<string, unknown> | null;
      tms_page?: Record<string, unknown> | null;
      updated_at: string;
    }>(`/page-structure?id=${id}`, {
      method: 'GET',
    });
  }

  async createPageStructure(pageStructure: {
    home?: Record<string, unknown> | null;
    text_management?: Record<string, unknown> | null;
    documents?: Record<string, unknown> | null;
    glossary?: Record<string, unknown> | null;
    blueprints?: Record<string, unknown> | null;
    typesandtags?: Record<string, unknown> | null;
    settings?: Record<string, unknown> | null;
    tms_page?: Record<string, unknown> | null;
  }): Promise<ApiResponse<{
    id: string;
    user_id: string;
    home: Record<string, unknown> | null;
    text_management: Record<string, unknown> | null;
    documents: Record<string, unknown> | null;
    glossary: Record<string, unknown> | null;
    blueprints: Record<string, unknown> | null;
    typesandtags?: Record<string, unknown> | null;
    settings?: Record<string, unknown> | null;
    tms_page?: Record<string, unknown> | null;
    updated_at: string;
  }>> {
    return this.request<{
      id: string;
      user_id: string;
      home: Record<string, unknown> | null;
      text_management: Record<string, unknown> | null;
      documents: Record<string, unknown> | null;
      glossary: Record<string, unknown> | null;
      blueprints: Record<string, unknown> | null;
      typesandtags?: Record<string, unknown> | null;
      settings?: Record<string, unknown> | null;
      tms_page?: Record<string, unknown> | null;
      updated_at: string;
    }>('/page-structure', {
      method: 'POST',
      body: JSON.stringify(pageStructure),
    });
  }

  async updatePageStructure(id: string, pageStructure: {
    home?: Record<string, unknown> | null;
    text_management?: Record<string, unknown> | null;
    documents?: Record<string, unknown> | null;
    glossary?: Record<string, unknown> | null;
    blueprints?: Record<string, unknown> | null;
    typesandtags?: Record<string, unknown> | null;
    settings?: Record<string, unknown> | null;
    tms_page?: Record<string, unknown> | null;
  }): Promise<ApiResponse<{
    id: string;
    user_id: string;
    home: Record<string, unknown> | null;
    text_management: Record<string, unknown> | null;
    documents: Record<string, unknown> | null;
    glossary: Record<string, unknown> | null;
    blueprints: Record<string, unknown> | null;
    typesandtags?: Record<string, unknown> | null;
    settings?: Record<string, unknown> | null;
    tms_page?: Record<string, unknown> | null;
    updated_at: string;
  }>> {
        
    return this.request<{
      id: string;
      user_id: string;
      home: Record<string, unknown> | null;
      text_management: Record<string, unknown> | null;
      documents: Record<string, unknown> | null;
      glossary: Record<string, unknown> | null;
      blueprints: Record<string, unknown> | null;
      typesandtags?: Record<string, unknown> | null;
      settings?: Record<string, unknown> | null;
      tms_page?: Record<string, unknown> | null;
      updated_at: string;
    }>('/page-structure', {
      method: 'PUT',
      body: JSON.stringify({ id, ...pageStructure }),
    });
  }

  async validateToken(): Promise<ApiResponse<LoginResponse>> {
    const token = this.getStoredToken();
    if (!token) {
      return {
        success: false,
        error: 'No token to validate',
      };
    }

    return this.request<LoginResponse>(API_CONFIG.ENDPOINTS.AUTH.VALIDATE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  /**
   * Exchange a Supabase session token for a custom JWT.
   * Used after email confirmation when we have a Supabase token but need our custom JWT.
   */
  async exchangeSession(supabaseToken: string): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>(API_CONFIG.ENDPOINTS.AUTH.EXCHANGE_SESSION, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${supabaseToken}`,
      },
    });
  }

  // Types methods
  async fetchTypes(gameId: string): Promise<ApiResponse<Array<{
    id: string;
    game_id: string;
    name: string;
    element_type: string;
    group_name?: string;
    description?: string;
    created_at: string;
    updated_at: string;
    data: TypeElement[];
  }>>> {
    return this.request<Array<{
      id: string;
      game_id: string;
      name: string;
      element_type: string;
      group_name?: string;
      description?: string;
      created_at: string;
      updated_at: string;
      data: TypeElement[];
    }>>(`/types?game_id=${gameId}`, {
      method: 'GET',
    });
  }

  async createType(typeData: {
    game_id: string;
    name: string;
    element_type: string;
    group?: string;
    description?: string;
    data: TypeElement[];

  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    element_type: string;
    group?: string;
    description?: string;
    created_at: string;
    updated_at: string;
    data: TypeElement[];

  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      element_type: string;
      group?: string;
      description?: string;
      created_at: string;
      updated_at: string;
      data: TypeElement[];

    }>('/types', {
      method: 'POST',
      body: JSON.stringify(typeData),
    });
  }

  async updateType(typeData: {
    id: string;
    game_id: string;
    name: string;
    element_type: string;
    group?: string;
    description?: string;
    data: TypeElement[];

  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    element_type: string;
    group?: string;
    description?: string;
    created_at: string;
    updated_at: string;
    data: TypeElement[];
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      element_type: string;
      group?: string;
      description?: string;
      created_at: string;
      updated_at: string;
      data: TypeElement[];
    }>('/types', {
      method: 'PUT',
      body: JSON.stringify(typeData),
    });
  }

  async deleteType(id: string, gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/types?id=${id}&game_id=${gameId}`, {
      method: 'DELETE',
    });
  }

  // Type Elements methods - DEPRECATED
  // Type elements are now handled through the type's data field
  async fetchTypeElements(typeId?: string, gameId?: string): Promise<ApiResponse<Array<{
    id: string;
    type_id: string;
    name: string;
    description?: string;
    created_at: string;
    updated_at: string;
    data: TypeElement[];
  }>>> {
    // This endpoint is deprecated - use fetchTypes instead
    throw new Error('fetchTypeElements is deprecated. Use fetchTypes instead.');
  }

  // Note: Type elements are now handled through the type's data field
  // This method is kept for backward compatibility but should not be used
  async createTypeElement(elementData: {
    type_id: string;
    name: string;
    description?: string;
  }): Promise<ApiResponse<{
    id: string;
    type_id: string;
    name: string;
    description?: string;
    created_at: string;
    updated_at: string;
    data: TypeElement[];
  }>> {
    // This endpoint is deprecated - use updateType instead
    throw new Error('createTypeElement is deprecated. Use updateType with the type\'s data field instead.');
  }

  // Note: Type elements are now handled through the type's data field
  // This method is kept for backward compatibility but should not be used
  async updateTypeElement(elementData: {
    id: string;
    type_id: string;
    name: string;
    data: TypeElement[];
    description?: string;
  }): Promise<ApiResponse<{
    id: string;
    type_id: string;
    name: string;
    description?: string;
    data: TypeElement[];
    created_at: string;
    updated_at: string;
  }>> {
    // This endpoint is deprecated - use updateType instead
    throw new Error('updateTypeElement is deprecated. Use updateType with the type\'s data field instead.');
  }

  // Note: Type elements are now handled through the type's data field
  // This method is kept for backward compatibility but should not be used
  async deleteTypeElement(id: string, typeId: string): Promise<ApiResponse<boolean>> {
    // This endpoint is deprecated - use updateType instead
    throw new Error('deleteTypeElement is deprecated. Use updateType with the type\'s data field instead.');
  }

  // Tags methods
  async fetchTags(gameId: string): Promise<ApiResponse<Array<{
    id: string;
    game_id: string;
    name: string;
    group?: string;
    description?: string;
    color?: string;
    created_at: string;
    updated_at: string;
  }>>> {
    return this.request<Array<{
      id: string;
      game_id: string;
      name: string;
      group?: string;
      description?: string;
      color?: string;
      created_at: string;
      updated_at: string;
    }>>(`/tags?game_id=${gameId}`, {
      method: 'GET',
    });
  }

  async createTag(tagData: {
    game_id: string;
    name: string;
    group?: string;
    description?: string;
    color?: string;
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    group?: string;
    description?: string;
    color?: string;
    created_at: string;
    updated_at: string;
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      group?: string;
      description?: string;
      color?: string;
      created_at: string;
      updated_at: string;
    }>('/tags', {
      method: 'POST',
      body: JSON.stringify(tagData),
    });
  }

  async updateTag(tagData: {
    id: string;
    game_id: string;
    name: string;
    group?: string;
    description?: string;
    color?: string;
  }): Promise<ApiResponse<{
    id: string;
    game_id: string;
    name: string;
    group?: string;
    description?: string;
    color?: string;
    created_at: string;
    updated_at: string;
  }>> {
    return this.request<{
      id: string;
      game_id: string;
      name: string;
      group?: string;
      description?: string;
      color?: string;
      created_at: string;
      updated_at: string;
    }>('/tags', {
      method: 'PUT',
      body: JSON.stringify(tagData),
    });
  }

  async deleteTag(id: string, gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/tags?id=${id}&game_id=${gameId}`, {
      method: 'DELETE',
    });
  }

  // Image upload methods
  async uploadImage(file: File, gameId: string): Promise<ApiResponse<{
    url: string;
    filename: string;
    originalName: string;
    size: number;
    mimetype: string;
  }>> {
    const formData = new FormData();
    formData.append('image', file);

    // Don't set Content-Type header for FormData - let browser set it with boundary
    const token = this.getStoredToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      // Add game_id as query parameter
      const response = await fetch(buildApiUrl(`/upload-image?game_id=${encodeURIComponent(gameId)}`), {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        }

        return {
          success: false,
          error: errorMessage,
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("Image upload error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async deleteImage(url: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>('/delete-image', {
      method: 'DELETE',
      body: JSON.stringify({ url }),
    });
  }

  async updateImageMetadata(imageId: string, customName?: string, imagekey?: string): Promise<ApiResponse<{
    id: string;
    name: string;
    imagekey: string;
    path: string;
    url: string;
    size: number;
    createdAt: string;
    updatedAt: string;
    mimetype: string;
    originalFilename: string;
  }>> {
    return this.request('/update-image-metadata', {
      method: 'PUT',
      body: JSON.stringify({
        image_id: imageId,
        custom_name: customName,
        imagekey: imagekey,
      }),
    });
  }

  async migrateImages(gameId: string): Promise<ApiResponse<{
    migrated: number;
    skipped: number;
    errors: number;
    total: number;
    errorDetails?: Array<{ filename: string; error: string }>;
  }>> {
    return this.request('/migrate-images', {
      method: 'POST',
      body: JSON.stringify({
        game_id: gameId,
      }),
    });
  }

  async checkMigrationNeeded(gameId: string): Promise<ApiResponse<{
    needsMigration: boolean;
    storageCount: number;
    databaseCount: number;
    unmigrated: number;
  }>> {
    return this.request(`/check-migration-needed?game_id=${encodeURIComponent(gameId)}`, {
      method: 'GET',
    });
  }

  async listImages(gameId: string): Promise<ApiResponse<{
    images: Array<{
      name: string;
      path: string;
      url: string;
      size: number;
      createdAt: string;
      updatedAt: string;
      mimetype: string;
    }>;
    total: number;
  }>> {
    return this.request<{
      images: Array<{
        name: string;
        path: string;
        url: string;
        size: number;
        createdAt: string;
        updatedAt: string;
        mimetype: string;
      }>;
      total: number;
    }>(`/list-images?game_id=${encodeURIComponent(gameId)}`, {
      method: 'GET',
    });
  }

  async getImageUsage(gameId: string): Promise<ApiResponse<{
    usage: Record<string, Array<{
      itemId: string;
      itemName: string;
      itemType: 'glossary' | 'document';
      field: string;
    }>>;
    totalImages: number;
    totalUsages: number;
  }>> {
    return this.request<{
      usage: Record<string, Array<{
        itemId: string;
        itemName: string;
        itemType: 'glossary' | 'document';
        field: string;
      }>>;
      totalImages: number;
      totalUsages: number;
    }>(`/image-usage?game_id=${encodeURIComponent(gameId)}`, {
      method: 'GET',
    });
  }

  async uploadImages(
    files: FileList | File[], 
    gameId: string,
    metadata?: Array<{ originalName: string; customName: string; imagekey: string }>
  ): Promise<ApiResponse<{
    uploaded: Array<{
      id: string;
      url: string;
      filename: string;
      path: string;
      originalName: string;
      customName: string;
      imagekey: string;
      size: number;
      mimetype: string;
    }>;
    uploadedCount: number;
    totalCount: number;
    errors?: Array<{
      filename: string;
      error: string;
    }>;
  }>> {
    const formData = new FormData();
    
    // Add all files to form data
    Array.from(files).forEach(file => {
      formData.append('images', file);
    });

    // Add metadata if provided
    if (metadata) {
      formData.append('metadata', JSON.stringify(metadata));
    }

    const token = this.getStoredToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(buildApiUrl(`/upload-images?game_id=${encodeURIComponent(gameId)}`), {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        }

        return {
          success: false,
          error: errorMessage,
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("Images upload error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async updateGame(gameId: string, updates: {
    name?: string;
    description?: string;
    settings?: Record<string, unknown>;
    folders?: GameFolderSection[];
    members?: GameMember[];
  }): Promise<ApiResponse<{
    id: string;
    name: string;
    description?: string;
    owner: string;
    settings?: Record<string, unknown>;
    created_at: string;
    last_updated: string;
    folders?: GameFolderSection[];
    members?: GameMember[];
  }>> {
    return this.request<{
      id: string;
      name: string;
      description?: string;
      owner: string;
      settings?: Record<string, unknown>;
      created_at: string;
      last_updated: string;
      members?: GameMember[];
      folders?: GameFolderSection[];
    }>(`${API_CONFIG.ENDPOINTS.DATA.GAMES}`, {
      method: 'PUT',
      body: JSON.stringify({
        id: gameId,
        ...updates
      }),
    });
  }

  async createGame(gameData: {
    name: string;
    description?: string;
    company_id: string;
    settings?: Record<string, unknown>;
  }): Promise<ApiResponse<{
    id: string;
    name: string;
    description?: string;
    owner: string;
    company_id: string;
    settings?: Record<string, unknown>;
    created_at: string;
    last_updated: string;
    folders?: GameFolderSection[];
    members?: GameMember[];
  }>> {
    return this.request<{
      id: string;
      name: string;
      description?: string;
      owner: string;
      company_id: string;
      settings?: Record<string, unknown>;
      created_at: string;
      last_updated: string;
      folders?: GameFolderSection[];
      members?: GameMember[];
    }>(`${API_CONFIG.ENDPOINTS.DATA.GAMES}`, {
      method: 'POST',
      body: JSON.stringify(gameData),
    });
  }

  async deleteGame(gameId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`${API_CONFIG.ENDPOINTS.DATA.GAMES}`, {
      method: 'DELETE',
      body: JSON.stringify({ id: gameId }),
    });
  }

  async getArchivePreview(gameId: string): Promise<ApiResponse<{
    game: { id: string; name: string; description: string };
    entities: {
      documents: { count: number; items: { id: string; name: string }[] };
      blueprints: { count: number; items: { id: string; name: string }[] };
      libraryItems: { count: number; items: { id: string; name: string }[] };
      dialogs: { count: number; items: { id: string; name: string }[] };
      types: { count: number; items: { id: string; name: string }[] };
      tags: { count: number; items: { id: string; name: string }[] };
      logicFlows: { count: number; items: { id: string; name: string }[] };
      tmsStrings: { count: number; items: { id: string; name: string }[] };
      translations: { count: number; items: { id: string; name: string }[] };
      uiElements: { count: number; items: { id: string; name: string }[] };
      relationships: { count: number; items: { id: string; name: string }[] };
      images: { count: number; items: { id: string; name: string }[] };
    };
    summary: {
      totalEntities: number;
      totalTranslations: number;
      totalRelationships: number;
      totalImages: number;
    };
  }>> {
    return this.request(`/archive-game?game_id=${encodeURIComponent(gameId)}`, {
      method: 'GET',
    });
  }

  async archiveGame(gameId: string, archiveSuffix?: string): Promise<ApiResponse<{
    archive_id: string;
    archive_name: string;
    storage_path: string;
    file_size: number;
    archived_entities: Record<string, number>;
    deleted_assets?: Record<string, number>;
  }>> {
    return this.request('/archive-game', {
      method: 'POST',
      body: JSON.stringify({
        game_id: gameId,
        archive_suffix: archiveSuffix,
      }),
    });
  }

  async updateCompany(companyId: string, updates: {
    name?: string;
    subscription?: string;
    settings?: Record<string, unknown>;
  }): Promise<ApiResponse<{
    id: string;
    name: string;
    subscription: string;
    created_at: string;
    settings?: Record<string, unknown>;
  }>> {
    return this.request<{
      id: string;
      name: string;
      subscription: string;
      created_at: string;
      settings?: Record<string, unknown>;
    }>(`${API_CONFIG.ENDPOINTS.DATA.COMPANIES}`, {
      method: 'PUT',
      body: JSON.stringify({
        id: companyId,
        ...updates
      }),
    });
  }

  async getProfile(userId?: string): Promise<ApiResponse<{
    id: string;
    full_name?: string;
    avatar_url?: string;
    settings?: Record<string, unknown>;
    username?: string;
    created_at: string;
    updated_at: string;
    Last_Online?: string;
    bio?: string;
    email: string;
  }>> {
    const url = userId 
      ? `${API_CONFIG.ENDPOINTS.DATA.PROFILES}?user_id=${userId}`
      : API_CONFIG.ENDPOINTS.DATA.PROFILES;
    
    return this.request<{
      id: string;
      full_name?: string;
      avatar_url?: string;
      settings?: Record<string, unknown>;
      username?: string;
      created_at: string;
      updated_at: string;
      Last_Online?: string;
      bio?: string;
      email: string;
    }>(url, {
      method: 'GET',
    });
  }

  async updateProfile(updates: {
      id?: string;
    email?: string;
    bio?: string;
    full_name?: string;
    avatar_url?: string;
    settings?: Record<string, unknown>;
    Last_Online?: string;
  }): Promise<ApiResponse<{
    id: string;
    email?: string;
    bio?: string;
    full_name?: string;
    avatar_url?: string;
    settings?: Record<string, unknown>;
    created_at: string;
    username?: string;
    updated_at: string;
    Last_Online?: string;
  }>> {
    return this.request<{
      id: string;
      email?: string;
      bio?: string;
      full_name?: string;
      avatar_url?: string;
      settings?: Record<string, unknown>;
      created_at: string;
      username?: string;
      updated_at: string;
      Last_Online?: string;
    }>(`${API_CONFIG.ENDPOINTS.DATA.PROFILES}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Invite a user to join a company
   * Creates a single-use invitation code for the specified email
   */
  async inviteUser(request: {
    email: string;
    role: string;
    companyId: string;
    gameIds?: string[];
  }): Promise<ApiResponse<{
    invitation_code: string;
    code_id: string;
    email: string;
    role: string;
    company_name: string;
    games_count: number;
    expires_at?: string;
  }>> {
    return this.request('/invite-user', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Lookup a user by email to check if they exist in LocaNest
   * Returns user information if found
   */
  async lookupUserByEmail(email: string): Promise<ApiResponse<{
    exists: boolean;
    user?: {
      id: string;
      email: string;
      username: string;
      full_name: string;
    };
  }>> {
    return this.request('/lookup-user', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Update a user's role and game access
   */
  async updateUser(request: {
    userId: string;
    companyId: string;
    role?: string; // Changed to string to support custom roles
    gameAccess?: Array<{
      gameId: string;
      gameName: string;
      hasAccess: boolean;
    }>;
  }): Promise<ApiResponse<{
    userId: string;
    companyId: string;
    updates: Record<string, any>;
  }>> {
    return this.request('/update-user', {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  /**
   * Remove a user from a company and all its games
   */
  async removeUser(request: {
    userId: string;
    companyId: string;
  }): Promise<ApiResponse<{
    userId: string;
    companyId: string;
    removedFrom: {
      company: boolean;
      games: number;
    };
  }>> {
    return this.request('/remove-user', {
      method: 'DELETE',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get all relationships for an entity (both incoming and outgoing)
   */
  async getEntityRelationships(
    entityId: string,
    entityType: string,
    gameId?: string
  ): Promise<ApiResponse<{
    referenced_by: Array<{
      source_id: string;
      source_type: string;
      relationship_context?: string;
    }>;
    references: Array<{
      target_id: string;
      target_type: string;
      relationship_context?: string;
    }>;
    referenced_by_count: number;
    references_count: number;
  }>> {
    const selectedGame = getSelectedGame();
    const finalGameId = gameId || selectedGame?.id;

    if (!finalGameId) {
      throw new Error('No game ID provided and no game selected');
    }

    return this.request(
      `/entity-relationships?game_id=${finalGameId}&entity_id=${entityId}&entity_type=${entityType}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Get entities that reference a target entity
   */
  async getReferencesToEntity(
    targetId: string,
    targetType: string,
    gameId?: string
  ): Promise<ApiResponse<{
    referenced_by: Array<{
      source_id: string;
      source_type: string;
      relationship_context?: string;
    }>;
    count: number;
  }>> {
    const selectedGame = getSelectedGame();
    const finalGameId = gameId || selectedGame?.id;

    if (!finalGameId) {
      throw new Error('No game ID provided and no game selected');
    }

    return this.request(
      `/entity-relationships?game_id=${finalGameId}&target_id=${targetId}&target_type=${targetType}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Get entities that a source entity references
   */
  async getReferencesFromEntity(
    sourceId: string,
    sourceType: string,
    gameId?: string
  ): Promise<ApiResponse<{
    references: Array<{
      target_id: string;
      target_type: string;
      relationship_context?: string;
    }>;
    count: number;
  }>> {
    const selectedGame = getSelectedGame();
    const finalGameId = gameId || selectedGame?.id;

    if (!finalGameId) {
      throw new Error('No game ID provided and no game selected');
    }

    return this.request(
      `/entity-relationships?game_id=${finalGameId}&source_id=${sourceId}&source_type=${sourceType}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Get all relationships for a game
   */
  async getAllGameRelationships(
    gameId?: string
  ): Promise<ApiResponse<Array<{
    game_id: string;
    source_id: string;
    source_type: string;
    target_id: string;
    target_type: string;
    relationship_context?: string;
    created_at?: string;
  }>>> {
    const selectedGame = getSelectedGame();
    const finalGameId = gameId || selectedGame?.id;

    if (!finalGameId) {
      throw new Error('No game ID provided and no game selected');
    }

    return this.request(
      `/entity-relationships?game_id=${finalGameId}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Rebuild all relationships for a game
   */
  async rebuildGameRelationships(
    gameId?: string
  ): Promise<ApiResponse<{
    totalProcessed: number;
    totalErrors: number;
    cleaned?: number;
    details: {
      glossary: { processed: number; errors: number };
      documents: { processed: number; errors: number };
    };
  }>> {
    const selectedGame = getSelectedGame();
    const finalGameId = gameId || selectedGame?.id;

    if (!finalGameId) {
      throw new Error('No game ID provided and no game selected');
    }

    return this.request(
      `/rebuild-relationships?game_id=${finalGameId}`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Complete user onboarding - creates profile, company, and template game OR joins via invitation code
   */
  async completeOnboarding(data: {
    first_name: string;
    last_name: string;
    username?: string;
    company_name?: string;
    invitation_code?: string;
  }): Promise<ApiResponse<{
    profile: {
      id: string;
      username?: string;
      full_name: string;
      email: string;
    };
    company: {
      id: string;
      name: string;
      subscription: string;
      members: Array<{ id: string; role: string }>;
    };
    game?: {
      id: string;
      name: string;
    };
    games?: Array<{
      id: string;
      name: string;
    }>;
    token?: string;
  }>> {
    return this.request(
      '/complete-onboarding',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Create a new profile
   */
  async createProfile(data: {
    user_id?: string;
    email: string;
    username?: string;
    full_name?: string;
  }): Promise<ApiResponse<{
    id: string;
    email: string;
    username?: string;
    full_name?: string;
    avatar_url?: string;
    bio?: string;
    settings?: Record<string, unknown>;
    created_at: string;
    updated_at: string;
  }>> {
    return this.request(
      '/profiles',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  // ==================== Invitation Code Methods ====================

  /**
   * Generate a new invitation code
   */
  async generateInvitationCode(data: {
    company_id: string;
    code_name: string;
    game_ids: string[];
    role: string;
    expires_at?: string;
    allowed_email_domains?: string[];
    is_single_use?: boolean;
    target_email?: string;
  }): Promise<ApiResponse<{
    id: string;
    company_id: string;
    code: string;
    code_name: string;
    created_by: string;
    game_ids: string[];
    role: string;
    expires_at?: string;
    allowed_email_domains?: string[];
    is_single_use?: boolean;
    target_email?: string;
    created_at: string;
  }>> {
    return this.request(
      '/company-invitation-codes',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Get all invitation codes for a company
   */
  async getInvitationCodes(company_id: string, include_expired?: boolean): Promise<ApiResponse<Array<{
    id: string;
    company_id: string;
    code: string;
    code_name: string;
    created_by: string;
    created_by_name: string;
    game_ids: string[];
    role: string;
    expires_at?: string;
    allowed_email_domains?: string[];
    is_single_use?: boolean;
    target_email?: string;
    status?: string;
    created_at: string;
    used_at?: string;
    used_by?: string;
    used_by_name?: string;
    usage_count?: number;
  }>>> {
    const params = new URLSearchParams({ company_id });
    if (include_expired !== undefined) {
      params.append('include_expired', String(include_expired));
    }
    return this.request(
      `/company-invitation-codes?${params.toString()}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Get pending invitations for a company
   */
  async getPendingInvitations(company_id: string): Promise<ApiResponse<Array<{
    id: string;
    company_id: string;
    code: string;
    code_name: string;
    created_by: string;
    created_by_name: string;
    game_ids: string[];
    role: string;
    expires_at?: string;
    target_email?: string;
    status: string;
    created_at: string;
  }>>> {
    const params = new URLSearchParams({ 
      company_id,
      action: 'get_pending'
    });
    return this.request(
      `/company-invitation-codes?${params.toString()}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Revoke an invitation code
   */
  async revokeInvitationCode(code_id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request(
      `/company-invitation-codes?code_id=${code_id}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Validate an invitation code (public, no auth required)
   */
  async validateInvitationCode(code: string, email: string): Promise<ApiResponse<{
    valid: boolean;
    code_id?: string;
    code_name?: string;
    company_id?: string;
    company_name?: string;
    game_names?: string[];
    game_ids?: string[];
    role?: string;
    expires_at?: string;
    is_single_use?: boolean;
  }>> {
    // This endpoint doesn't require authentication, so we make a direct fetch
    const url = buildApiUrl('/validate-invitation-code');
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Validation failed',
        };
      }

      return {
        success: data.success,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Get users who joined via a specific invitation code
   */
  async getInvitationCodeUsers(code_id: string): Promise<ApiResponse<Array<{
    user_id: string;
    username: string;
    full_name?: string;
    email: string;
    used_at: string;
  }>>> {
    const params = new URLSearchParams({ 
      code_id,
      action: 'get_users'
    });
    return this.request(
      `/company-invitation-codes?${params.toString()}`,
      {
        method: 'GET',
      }
    );
  }

  // ==================== Company Roles Methods ====================

  /**
   * Get all roles for a company (built-in + custom)
   */
  async getCompanyRoles(company_id: string): Promise<ApiResponse<Array<{
    id: string;
    company_id: string;
    role_name: string;
    is_built_in: boolean;
    description?: string;
    data?: Record<string, any>; // Additional metadata like colors, icons, etc.
    permissions: Record<string, boolean>;
    created_at: string;
  }>>> {
    const params = new URLSearchParams({ company_id });
    return this.request(
      `/company-roles?${params.toString()}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Create a new custom role with permissions
   */
  async createCompanyRole(data: {
    company_id: string;
    role_name: string;
    description?: string;
    data?: Record<string, any>; // Additional metadata like colors, icons, etc.
    permissions: Record<string, boolean>;
  }): Promise<ApiResponse<{
    id: string;
    company_id: string;
    role_name: string;
    is_built_in: boolean;
    description?: string;
    data?: Record<string, any>;
    permissions: Record<string, boolean>;
  }>> {
    return this.request(
      '/company-roles',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Update role permissions
   */
  async updateRolePermissions(data: {
    role_id: string;
    permissions: Record<string, boolean>;
    description?: string;
    data?: Record<string, any>; // Additional metadata like colors, icons, etc.
  }): Promise<ApiResponse<{ message: string }>> {
    return this.request(
      '/company-roles',
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Delete a custom role
   */
  async deleteCompanyRole(role_id: string): Promise<ApiResponse<{ message: string }>> {
    const params = new URLSearchParams({ role_id });
    return this.request(
      `/company-roles?${params.toString()}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Get list of reserved company names (public, no auth required)
   */
  async getReservedCompanyNames(): Promise<ApiResponse<string[]>> {
    const url = buildApiUrl('/reserved-company-names');
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to fetch reserved names',
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Fetch favorites for a specific user and game
   */
  async fetchFavorites(userId: string, gameId: string): Promise<ApiResponse<Array<{
    id: string;
    user_id: string;
    item_id: string;
    item_type: 'library' | 'document' | 'blueprint';
    game_id: string;
    created_at: string;
  }>>> {
    const params = new URLSearchParams({ user_id: userId, game_id: gameId });
    return this.request(
      `/favorites?${params.toString()}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Add an item to favorites
   */
  async addFavorite(data: {
    user_id: string;
    game_id: string;
    item_id: string;
    item_type: 'library' | 'document' | 'blueprint';
  }): Promise<ApiResponse<{
    id: string;
    user_id: string;
    item_id: string;
    item_type: 'library' | 'document' | 'blueprint';
    game_id: string;
    created_at: string;
  }>> {
    return this.request(
      '/favorites',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Remove an item from favorites
   */
  async removeFavorite(userId: string, itemId: string): Promise<ApiResponse<{ message: string }>> {
    const params = new URLSearchParams({ user_id: userId, item_id: itemId });
    return this.request(
      `/favorites?${params.toString()}`,
      {
        method: 'DELETE',
      }
    );
  }

  // ============================================================================
  // BLUEPRINT SEVERITY & BULK UPDATE METHODS
  // ============================================================================

  /**
   * Fetch glossary items affected by a blueprint version
   */
  async fetchBlueprintAffectedItems(
    blueprintId: string,
    major: number,
    minor: number,
    patch: number
  ): Promise<ApiResponse<any>> {
    return this.request(`/blueprint-affected-items?blueprint_id=${blueprintId}&major=${major}&minor=${minor}&patch=${patch}`, {
      method: 'GET',
    });
  }

  /**
   * Bulk update glossary items to a new blueprint version
   */
  async bulkUpdateGlossaryItems(
    blueprintId: string,
    version: { major: number; minor: number; patch: number },
    itemIds: string[],
    strategy: 'merge' | 'overwrite' = 'merge'
  ): Promise<ApiResponse<any>> {
    return this.request('/blueprint-bulk-update', {
      method: 'POST',
      body: JSON.stringify({
        blueprint_id: blueprintId,
        version,
        item_ids: itemIds,
        strategy
      }),
    });
  }

  /**
   * Auto-sync glossary items with mismatched versions (no structural changes)
   */
  async autoSyncBlueprintItems(blueprintId: string): Promise<ApiResponse<any>> {
    return this.request('/blueprint-auto-sync', {
      method: 'POST',
      body: JSON.stringify({
        blueprint_id: blueprintId
      }),
    });
  }

  // ============================================================================
  // TUTORIAL SYSTEM METHODS
  // ============================================================================

  /**
   * Fetch all tutorial tasks with user's progress
   */
  async getTutorialTasks(): Promise<ApiResponse<any[]>> {
    return this.request('/tutorial-tasks', {
      method: 'GET',
    });
  }

  /**
   * Create a new tutorial task (admin only)
   */
  async createTutorialTask(task: {
    title: string;
    description: string;
    task_type: string;
    task_data?: Record<string, any>;
    role_applicability?: string[];
    order_index?: number;
    is_active?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request('/tutorial-tasks', {
      method: 'POST',
      body: JSON.stringify(task),
    });
  }

  /**
   * Update an existing tutorial task (admin only)
   */
  async updateTutorialTask(taskId: string, updates: {
    title?: string;
    description?: string;
    task_type?: string;
    task_data?: Record<string, any>;
    role_applicability?: string[];
    order_index?: number;
    is_active?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request(`/tutorial-tasks?task_id=${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete a tutorial task (admin only)
   */
  async deleteTutorialTask(taskId: string): Promise<ApiResponse<void>> {
    return this.request(`/tutorial-tasks?task_id=${taskId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Fetch user's tutorial progress
   */
  async getTutorialProgress(): Promise<ApiResponse<any[]>> {
    return this.request('/tutorial-progress', {
      method: 'GET',
    });
  }

  /**
   * Mark a tutorial task as complete
   */
  async markTaskComplete(taskId: string): Promise<ApiResponse<void>> {
    return this.request('/tutorial-progress', {
      method: 'POST',
      body: JSON.stringify({
        task_id: taskId,
        action: 'complete'
      }),
    });
  }

  /**
   * Skip a tutorial task
   */
  async skipTask(taskId: string): Promise<ApiResponse<void>> {
    return this.request('/tutorial-progress', {
      method: 'POST',
      body: JSON.stringify({
        task_id: taskId,
        action: 'skip'
      }),
    });
  }

  /**
   * Reset tutorial progress (admin only)
   */
  async resetTutorialProgress(userId?: string): Promise<ApiResponse<void>> {
    const query = userId ? `?user_id=${userId}` : '';
    return this.request(`/tutorial-progress${query}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // COMPANY API KEYS METHODS
  // ============================================================================

  /**
   * Get company API key
   */
  async getCompanyApiKey(companyId: string): Promise<ApiResponse<any>> {
    return this.request(`/company-api-keys?company_id=${companyId}`, {
      method: 'GET',
    });
  }

  /**
   * Create/Generate a new company API key
   */
  async createCompanyApiKey(companyId: string, name: string): Promise<ApiResponse<any>> {
    return this.request('/company-api-keys', {
      method: 'POST',
      body: JSON.stringify({
        company_id: companyId,
        name: name,
      }),
    });
  }

  /**
   * Delete/Revoke a company API key
   */
  async deleteCompanyApiKey(companyId: string): Promise<ApiResponse<void>> {
    return this.request('/company-api-keys', {
      method: 'DELETE',
      body: JSON.stringify({
        company_id: companyId,
      }),
    });
  }

  // ============================================================================
  // ITEM LOCK METHODS
  // ============================================================================

  /**
   * Get lock status for an item or all locks for a game
   */
  async getLockStatus(
    gameId: string,
    itemId?: string,
    itemType?: 'object' | 'dialog' | 'document' | 'template' | 'flow'
  ): Promise<ApiResponse<{ isLocked: boolean; lock: ItemLock | null } | ItemLock[]>> {
    const params = new URLSearchParams({ game_id: gameId });
    if (itemId) params.append('item_id', itemId);
    if (itemType) params.append('item_type', itemType);
    
    return this.request(`/item-locks?${params.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Get all locks for a game
   */
  async getGameLocks(gameId: string): Promise<ApiResponse<ItemLock[]>> {
    return this.request(`/item-locks?game_id=${gameId}`, {
      method: 'GET',
    }, true); // Silent mode - don't show toast notifications for background lock fetches
  }

  /**
   * Acquire a lock on an item
   */
  async acquireLock(
    itemId: string,
    itemType: 'object' | 'dialog' | 'document' | 'template' | 'flow',
    gameId: string
  ): Promise<ApiResponse<ItemLock>> {
    return this.request('/item-locks', {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        item_type: itemType,
        game_id: gameId,
      }),
    });
  }

  /**
   * Force acquire a lock on an item (kick another user)
   */
  async forceLock(
    itemId: string,
    itemType: 'object' | 'dialog' | 'document' | 'template' | 'flow',
    gameId: string
  ): Promise<ApiResponse<ItemLock>> {
    return this.request('/item-locks', {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        item_type: itemType,
        game_id: gameId,
        force: true,
      }),
    });
  }

  /**
   * Refresh/extend a lock (heartbeat)
   */
  async refreshLock(
    itemId: string,
    itemType: 'object' | 'dialog' | 'document' | 'template' | 'flow'
  ): Promise<ApiResponse<ItemLock>> {
    return this.request('/item-locks', {
      method: 'PUT',
      body: JSON.stringify({
        item_id: itemId,
        item_type: itemType,
      }),
    }, true); // Silent mode - don't show toast notifications for background heartbeats
  }

  /**
   * Release a lock on an item
   */
  async releaseLock(
    itemId: string,
    itemType: 'object' | 'dialog' | 'document' | 'template' | 'flow'
  ): Promise<ApiResponse<{ message: string }>> {
    const params = new URLSearchParams({
      item_id: itemId,
      item_type: itemType,
    });
    return this.request(`/item-locks?${params.toString()}`, {
      method: 'DELETE',
    });
  }
}

// Item Lock type for API responses
export interface ItemLock {
  id: string;
  item_id: string;
  item_type: 'object' | 'dialog' | 'document' | 'template' | 'flow';
  game_id: string;
  locked_by: string;
  locked_at: string;
  expires_at: string;
  profiles?: {
    id: string;
    full_name: string | null;
    username: string | null;
    email: string | null;
    avatar_url: string | null;
  };
}

export const apiService = new ApiService();
export default apiService;
